/**
 * How the example job's application is filled, both ways the extension
 * offers: Autofill from resume, free, with Jordan's details, saved
 * answers and the resume as they stored it; and Autofill, one tailor,
 * which tailors the resume to the posting and, in the same tailor,
 * answers the form's written questions from the person's own resume and
 * the posting (llm_gateway's answers). What each leaves for the person is what
 * the classifier (libs/crates/forms) leaves: a street address no resume
 * says, and, for the free fill, anything that needs writing. Pure data,
 * as made up as the rest of lib/example.ts.
 */

/** The two fills: from the stored resume, or with a tailor. */
export type FillMode = "resume" | "tailor";

export const FILL_MODES: readonly FillMode[] = ["tailor", "resume"];

/** A fill as its button names it, what it spends, and what pressing it does. */
export const FILL_MODE_COPY: Record<FillMode, { name: string; cost: string; steps: string[] }> = {
    resume: {
        name: "Autofill from resume",
        cost: "Free, always",
        steps: [
            "With the extension on the application, press Autofill from resume.",
            "Your name, contact details, links and saved answers go in.",
            "The resume you stored is attached as it is.",
        ],
    },
    tailor: {
        name: "Autofill",
        cost: "Uses a tailor",
        steps: [
            "With the extension on the application, press Autofill.",
            "Your resume is tailored to the posting: reworded and reordered, nothing added.",
            "The form's questions are answered from your resume and the posting, then it all goes in.",
        ],
    },
};

/** The example posting, as the tailor reads it. */
export const EXAMPLE_POSTING_ABOUT =
    "tessel is hiring a Staff Platform Engineer to lead the Kubernetes platform its product teams ship on.";

/** What a fill put in a field, and where it came from. */
export interface ExampleAnswer {
    value: string;
    from?: string;
}

export interface ExampleField {
    label: string;
    input: "text" | "select" | "file" | "long";
    /** What each fill puts in, or null where it leaves the field for the person. */
    fills: Record<FillMode, ExampleAnswer | null>;
    /** Why the field is the person's, where a fill leaves it. */
    yours?: string;
}

function both(answer: ExampleAnswer): Record<FillMode, ExampleAnswer> {
    return { resume: answer, tailor: answer };
}

const NEEDS_WRITING = "Needs writing. Autofill the rest answers it, for one tailor.";
const FROM_LARKSPUR = "Written from Jordan's resume: their work at Larkspur Freight";

/** The application form for the example job, top to bottom. */
export const EXAMPLE_FORM: readonly ExampleField[] = [
    { label: "First name", input: "text", fills: both({ value: "Jordan" }) },
    { label: "Last name", input: "text", fills: both({ value: "Lee" }) },
    { label: "Email", input: "text", fills: both({ value: "jordan@jordanlee.example" }) },
    { label: "Phone", input: "text", fills: both({ value: "(303) 555-0142" }) },
    { label: "Location", input: "text", fills: both({ value: "Denver, CO" }) },
    {
        label: "Street address",
        input: "text",
        fills: { resume: null, tailor: null },
        yours: "No resume says it.",
    },
    { label: "Website", input: "text", fills: both({ value: "jordanlee.example" }) },
    {
        label: "Resume/CV",
        input: "file",
        fills: {
            resume: { value: "Jordan_Lee_Resume.pdf", from: "The file Jordan uploaded, as it is" },
            tailor: {
                value: "Resume.pdf",
                from: "Tailored for this posting, checked against Jordan's own",
            },
        },
    },
    {
        label: "Are you authorized to work in the United States?",
        input: "select",
        fills: both({ value: "Yes", from: "A saved answer" }),
    },
    {
        label: "Will you now or in the future need visa sponsorship?",
        input: "select",
        fills: both({ value: "No", from: "A saved answer" }),
    },
    {
        label: "Which infrastructure as code tools have you used, and at what scale?",
        input: "text",
        fills: {
            resume: null,
            tailor: {
                value: "Terraform, for every change to 40 Kubernetes clusters on AWS.",
                from: FROM_LARKSPUR,
            },
        },
        yours: NEEDS_WRITING,
    },
    {
        label: "Tell us about a platform you have led.",
        input: "long",
        fills: {
            resume: null,
            tailor: {
                value: "At Larkspur Freight I own the Kubernetes platform: 40 clusters on AWS, every change made through Terraform. I led our move to Argo CD, which cut release time from a day to 20 minutes, and I mentor four engineers and lead the on-call rotation.",
                from: FROM_LARKSPUR,
            },
        },
        yours: NEEDS_WRITING,
    },
    {
        label: "Why do you want to work at tessel?",
        input: "long",
        fills: {
            resume: null,
            tailor: {
                value: "This role asks for what I do every day: Kubernetes at scale, Terraform, and leading platform work. At Larkspur Freight I run 40 clusters on AWS and led the move to Argo CD, and I want to bring that to tessel's platform team.",
                from: "Written from the posting and Jordan's resume",
            },
        },
        yours: NEEDS_WRITING,
    },
];

/** The fields a fill leaves for the person. */
export function leftFor(mode: FillMode): readonly ExampleField[] {
    return EXAMPLE_FORM.filter((field) => field.fills[mode] === null);
}

/** What a fill leaves, in a line. */
export function leftSummary(mode: FillMode): string {
    const left = leftFor(mode).length;
    const filled = EXAMPLE_FORM.length - left;
    return `${filled} of ${EXAMPLE_FORM.length} filled, ${left} left for Jordan`;
}
